/*
 * @providesModule TelemetryUtils
 */

import type {GlobalContext} from 'TelemetryTypes';
import {NativeModules} from 'react-native';
import SkillzPackageVersion from 'SkillzPackageVersion';
import CurrentUserStore from 'CurrentUserStore';
import I18n from 'INTL';
import NavigationManager from 'NavigationManager';

type SafeMeta = {scope: string; integration: string; event: string};

export const safelyExecute = <T>(fn: () => T, meta: SafeMeta): T | undefined => {
  try {
    return fn();
  } catch (error) {
    console.error('[telemetry] integration error', {...meta, error});
    return undefined;
  }
};

export const getGlobalContext = (): GlobalContext => {
  return {
    skillz_sdk_version: getSkillzSdkVersion(),
    event_timestamp: Date.now(),
    ...getGameDetails(),
    ...getScreenNavigationContext(),
  };
};

const getSkillzSdkVersion = (): string => {
  try {
    return SkillzPackageVersion || '0.0.0';
  } catch (error) {
    console.error('[telemetry] Error getting app version:', error);
    return '0.0.0';
  }
};

const getGameDetails = () => {
  try {
    const SkillzModule = NativeModules.SkillzModule;
    return {
      game_id: SkillzModule.App.GameId,
      game_name: SkillzModule.App.Name,
    };
  } catch (error) {
    console.error('[telemetry] Error getting app name:', error);
    return {};
  }
};

export const getUserWalletDetails = () => {
  try {
    const cachedUserData = CurrentUserStore.getCurrentCachedUserData();
    if (!cachedUserData) {
      console.warn('[telemetry] No cached user data available for wallet details');
      return {
        cash_balance: 0,
        bonus_balance: 0,
        z_balance: 0,
        ticketz_balance: 0,
        total_balance: 0,
      };
    }

    return {
      cash_balance: cachedUserData?.real_cash_balance || 0,
      bonus_balance: cachedUserData?.bonus_balance || 0,
      z_balance: cachedUserData?.z_balance || 0,
      ticketz_balance: cachedUserData?.ticketz?.balance || 0,
      total_balance: parseFloat(
        ((cachedUserData?.real_cash_balance || 0) + (cachedUserData?.bonus_balance || 0)).toFixed(2)
      ),
    };
  } catch (error) {
    console.error('[telemetry] Error getting wallet details:', error);
    return {
      cash_balance: 0,
      bonus_balance: 0,
      z_balance: 0,
      ticketz_balance: 0,
      total_balance: 0,
    };
  }
};

export const getUserDetails = () => {
  try {
    const cachedUserData = CurrentUserStore.getCurrentCachedUserData();
    const cachedPersonalInfo = CurrentUserStore.getCurrentCachedPersonalInfo();
    const locale = I18n.getDeviceLocale() || 'en-US';
    const isGuestUser = !CurrentUserStore.isSavedAccount();

    const userDetails = {
      user_id: '',
      user_name: '',
      user_property_timestamp: Date.now(),
      Locale: locale,
      email: '',
      phone: '',
      name: '',
      isGuestUser,
    };

    if (cachedUserData) {
      userDetails.user_id = cachedUserData.id || '';
      userDetails.user_name = cachedUserData.username || '';
      userDetails.email = cachedUserData.email || '';
      userDetails.name = cachedUserData.username || '';
    }

    if (cachedPersonalInfo) {
      userDetails.phone = cachedPersonalInfo.phone_number || '';
      if (!userDetails.email && cachedPersonalInfo.email) {
        userDetails.email = cachedPersonalInfo.email;
      }
    }

    return userDetails;
  } catch (error) {
    console.error('[telemetry] Error getting user details:', error);
    return {
      user_id: '',
      user_name: '',
      user_property_timestamp: Date.now(),
      Locale: 'en-US',
      email: '',
      phone: '',
      name: '',
      isGuestUser: '',
    };
  }
};

export const getCurrentScreenName = (): string => {
  try {
    return NavigationManager.getCurrentScreenIdentifier();
  } catch (error) {
    console.error('[telemetry] Error getting current screen name:', error);
    return '';
  }
};

export const getPreviousScreenName = (): string => {
  try {
    return NavigationManager.getPreviousScreenIdentifier();
  } catch (error) {
    console.error('[telemetry] Error getting previous screen name:', error);
    return '';
  }
};

export const getScreenNavigationContext = () => {
  try {
    const currentScreen = getCurrentScreenName();
    const previousScreen = getPreviousScreenName();

    return {
      current_screen: currentScreen,
      click_source: previousScreen,
    };
  } catch (error) {
    console.error('[telemetry] Error getting screen navigation context:', error);
    return {
      current_screen: '',
      click_source: '',
    };
  }
};
